import type { Auftrag, PdpDetails } from '@awf/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { ErrorState, LoadingState } from '../ui/states.js';
import { alsHtml } from './als-html.js';
import stile from './befund-bloecke.module.css';
import website from './website-text.module.css';

/**
 * Woher die Review-Ansicht den Originaltext der bestehenden Seite holt (Rueckmeldung Theresa
 * 2026-11-02): aus den Befund-Details, die fuer die Analyse schon gelesen wurden -- kein neuer
 * Crawl. Eine Optimierung aus einer Ratgeber-Analyse liest ueber die Seite der Analyse, eine
 * PDP-Optimierung ueber ihren eigenen Befund. Eine Neuerstellung hat kein Original: `null`.
 */
export type OriginalQuelle =
  { art: 'pdp'; runId: string } | { art: 'analyse'; runId: string; auditId: string };

export function originalQuelle(auftrag: Auftrag, runId: string): OriginalQuelle | null {
  if (auftrag.typ === 'pdp') return auftrag.befund?.auditId ? { art: 'pdp', runId } : null;
  if (auftrag.typ === 'ratgeber' && auftrag.ausAnalyseRunId && auftrag.ausAuditId) {
    return { art: 'analyse', runId: auftrag.ausAnalyseRunId, auditId: auftrag.ausAuditId };
  }
  return null;
}

type Zustand =
  | { art: 'zu' }
  | { art: 'laedt' }
  | { art: 'fehler'; text: string }
  | { art: 'daten'; originaltext: string };

/**
 * Eingeklappt wie "Originaltext anzeigen" in der Analyse (`BefundBericht`), gleiche Optik.
 * Geladen wird erst beim ersten Aufklappen: Jeder Abruf geht ueber das Lese-Kontingent und
 * n8n, und die meisten Runden brauchen das Original nicht.
 */
export function OriginaltextAufklapper({
  quelle,
  holen,
}: {
  quelle: OriginalQuelle;
  holen: (quelle: OriginalQuelle) => Promise<PdpDetails>;
}) {
  const [zustand, setZustand] = useState<Zustand>({ art: 'zu' });
  // Eine spaete Antwort nach dem Verlassen der Seite darf keinen Zustand mehr setzen.
  const aktiv = useRef(true);
  useEffect(() => {
    aktiv.current = true;
    return () => {
      aktiv.current = false;
    };
  }, []);

  function laden() {
    setZustand({ art: 'laedt' });
    holen(quelle).then(
      (d) => {
        if (aktiv.current) setZustand({ art: 'daten', originaltext: d.originaltext });
      },
      (f: unknown) => {
        if (aktiv.current) {
          setZustand({
            art: 'fehler',
            text: f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT,
          });
        }
      },
    );
  }

  const html = useMemo(
    () => (zustand.art === 'daten' ? { __html: alsHtml(zustand.originaltext) } : null),
    [zustand],
  );

  return (
    <details
      className={stile.originalAufklapper}
      onToggle={(e) => {
        if (e.currentTarget.open && zustand.art === 'zu') laden();
      }}
    >
      <summary>Originaltext anzeigen</summary>
      {zustand.art === 'laedt' && <LoadingState text="Der Originaltext wird geladen …" />}
      {zustand.art === 'fehler' && <ErrorState text={zustand.text} onWiederholen={laden} />}
      {zustand.art === 'daten' &&
        (html && zustand.originaltext ? (
          <div
            className={`${website.websiteText} ${stile.originaltext}`}
            dangerouslySetInnerHTML={html}
          />
        ) : (
          <p className={stile.leise}>Kein Originaltext hinterlegt.</p>
        ))}
    </details>
  );
}
